/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IMfrmProductRecommendationCarouselData } from './mfrm-product-recommendation-carousel.data';
import { IMfrmProductRecommendationCarouselProps } from './mfrm-product-recommendation-carousel.props.autogenerated';

import getUnbxdRecommendationData, { GetUnbxdRecommendationsDataInput } from '../../actions/get-unbxd-product-recommendations.action';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { UnbxdProductCarouselItem } from './components/unbxd-product-carousel-item';
import { addThrottledEvent } from '@msdyn365-commerce-modules/utilities';
import _getViewPort from '../../Utilities/get-view-port';
import MsDyn365, { ICoreContext } from '@msdyn365-commerce/core';
import { SimpleProduct, SalesLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getPagePath } from '../../Utilities/get-page-path';
import { getActivePricesAsync, getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import partialRight from 'lodash/partialRight';
import { getUtagProductCategory } from '../../Utilities/get-utag-product-category';
import { getUtagVariantId } from '../../Utilities/get-utag-variant-id';
import { createUtagQueue } from '../../Utilities/create-utag-queue';
import { hasArray } from "../../Utilities/emtpyChecker";
import { getSetWmapCartLines } from "../../Utilities/cart-utils";
import { removeSpecialChars } from '../../Utilities/allRegex';


export interface IMfrmProductRecommendationCarouselViewProps extends IMfrmProductRecommendationCarouselProps<IMfrmProductRecommendationCarouselData> {
    carouselItems: React.ReactNode[] | null;
    carouselHeading?: string;
    isMobile: boolean;
    currentPageName: string;
}
interface IMfrmProductRecommendationCarouselState {
    unbxdRecommendationData: any[] | undefined;
    widgetTitle?: string | undefined;
    count?: number | undefined;
    isMobile: boolean;
    unbxdRequestId: string;
}
export const getProductURLForUnbxd = (product: SimpleProduct | undefined, context: ICoreContext, wmapPageUrlSuffix: string): string => {
    let productUrl = getProductPageUrlSync(removeSpecialChars(product?.Name!, context) || '', product?.RecordId || 0, context?.actionContext, undefined);
    productUrl = productUrl?.replace(`/${wmapPageUrlSuffix}`, '');
    return `${context.request.url.requestUrl.origin}${productUrl}?variantid=${product?.RecordId}`;
};
class MfrmProductRecommendationCarousel extends React.PureComponent<IMfrmProductRecommendationCarouselProps<IMfrmProductRecommendationCarouselData>, IMfrmProductRecommendationCarouselState> {
    private resizeThrottledEventHandler?: (event: Event) => void;
    private viewedItems: any[];
    private utagQueue: object[] | undefined;
    private stopQueuing: (() => void) | undefined;
    private currentPageType: string;
    constructor(props: IMfrmProductRecommendationCarouselProps<IMfrmProductRecommendationCarouselData> & IMfrmProductRecommendationCarouselState) {
        super(props);
        this.state = {
            count: 0,
            unbxdRecommendationData: [],
            widgetTitle: "",
            isMobile: false,
            unbxdRequestId: ''
        };
        this.viewedItems = [];
        this.currentPageType = "";
        const createUtagQueueResult = createUtagQueue('product_impression_position', 1000);
        if (createUtagQueueResult) {
            this.utagQueue = createUtagQueueResult.utagQueue;
            this.stopQueuing = createUtagQueueResult.stopQueuing;
        }
    }
    public async componentDidMount(): Promise<void> {

        this.currentPageType = getPagePath()?.split('/')[1].toLowerCase();
        void this._getUnbxdRecommendationsData();
        if (MsDyn365.isBrowser) {
            this.updateViewPort(this.props.context);
        }
    }
    public async componentDidUpdate(): Promise<void> {
        if (MsDyn365.isBrowser) {
            this.resizeThrottledEventHandler = addThrottledEvent(window, 'resize', () => this.updateViewPort(this.props.context));
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('resize', this.resizeThrottledEventHandler!, false);
            this.stopQueuing!();
        }
    }

    private isWmap = (): boolean => {
        const pathName = this.props.context.request && this.props.context.request.url.requestUrl.pathname?.toLowerCase().split('/');
        const makeaPaymentLink = this.props.context?.app.config.wmapPageUrlSuffix?.toLowerCase();
        return pathName && makeaPaymentLink && pathName?.includes(makeaPaymentLink);
    };
    private updateViewPort = (context: ICoreContext): void => {
        const viewport = _getViewPort(context);
        if (viewport === 'xs' || viewport === 'sm') {
            this.setState({
                isMobile: true
            });
        } else {
            this.setState({
                isMobile: false
            });
        }
    };

    private handleUnbxdClickEvent = async (productUrl: string, productID: string, position: number, product: any) => {
        const { config: { productListPage } } = this.props;
        const productSpecificationData = await getAttributeValuesAsync(
            { callerContext: this.props.context.actionContext, queryResultSettings: {} },
            product.MasterProductId,
            this.props.context.request.apiSettings.channelId,
            this.props.context.request.apiSettings.catalogId || 0
        );
        const brand = productSpecificationData.find(attr => attr.Name === 'Brand')?.TextValue;
        const pageType = this.props.context.actionContext.requestContext.urlTokens.pageType;

        //@ts-ignore
        utag.link({
            'event': 'product-click',
            'enhanced_action': 'product_click',
            'event_category': 'ecommerce',
            'event_action': 'product click',
            'event_label': `${productID}: ${product.ProductName}`,
            'event_noninteraction': 'false',
            'product_list_name': [`Recommended Products: ${productListPage ? productListPage.trim() : this.isWmap() ? 'WMAP' : pageType === 'Product' ? 'Product' : pageType === 'Category' ? 'Category' : this.currentPageType}`], //for list name use ‘Recommended Products: Cart’ on the cart page and ‘Recommended Products: Order Confirmation’ on the order complete page
            'product_position': [`${position}`],
            'product_id': [`${product.uniqueId}`],
            'product_name': [`${product.ProductName}`],
            'product_category': [`${await getUtagProductCategory(
                this.props.context.actionContext,
                this.props.context.request.apiSettings.channelId,
                this.props.context.request.apiSettings.catalogId,
                product.Unbxd_DefaultVariantID)
                }`],
            'product_brand': [`${brand}`],
            'product_variant': [`${await getUtagVariantId(
                product.MasterProductId,
                this.props.context.request.apiSettings.channelId,
                this.props.context.actionContext,
                Number(product.Unbxd_DefaultVariantID), `${productID}`)}`],
            // adding the below variables for unbxd
            'unbxd_page_type': this.props.config.pageType.toUpperCase(),
            'unbxd_experience_widget': this.props.config.widgetNumber?.toUpperCase(),
            'd365_product_id': [`${product.MasterProductId}`],
            'd365_product_variant': [`${product.Unbxd_DefaultVariantID}`]
        });

        this.currentPageType === 'orderconfirmation' ? window.open(productUrl, "_blank") : window.location.href = productUrl;
    };

    private handleViewEvent = async (position: number, product: any, products: any[]) => {
        const productID = product.uniqueId;
        const unbxd_DefaultVariantID = product.Unbxd_DefaultVariantID;
        const getUtagProductCategoryProps = {
            actionContext: this.props.context.actionContext,
            channelId: this.props.context.request.apiSettings.channelId,
            catalogId: this.props.context.request.apiSettings.catalogId,
            RecordId: unbxd_DefaultVariantID
        };
        const widgetNumber = this.props.config.widgetNumber;
        const unbxdPageType = this.props.config.pageType;


        if (!this.viewedItems.includes(product.Unbxd_DefaultVariantID)) {
            this.viewedItems.push(product.Unbxd_DefaultVariantID);

            const productSpecificationData = await getAttributeValuesAsync(
                { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                product.MasterProductId,
                this.props.context.request.apiSettings.channelId,
                this.props.context.request.apiSettings.catalogId || 0
            );
            const brand = productSpecificationData.find(attr => attr.Name === 'Brand')?.TextValue;
            const variant = await getUtagVariantId(product.MasterProductId, getUtagProductCategoryProps.channelId, getUtagProductCategoryProps.actionContext, Number(getUtagProductCategoryProps.RecordId), `${productID}`);
            const prices = await getActivePricesAsync(
                { callerContext: this.props.context.actionContext },
                {
                    ChannelId: this.props.context.actionContext.requestContext.apiSettings.channelId,
                    CatalogId: this.props.context.actionContext.requestContext.apiSettings.catalogId
                },
                [Number(product.Unbxd_DefaultVariantID)],
                new Date(),
                null,
                [],
                true
            );
            const pageType = this.props.context.actionContext.requestContext.urlTokens.pageType;
            const { config: { productListPage } } = this.props;

            this.utagQueue!.push({
                'event': 'product-impression',
                'event_category': 'ecommerce',
                'event_action': 'product impression',
                'enhanced_action': 'product_impression',
                'event_label': `${getPagePath()}`,
                'event_noninteraction': 'true',
                'product_impression_list_name': [`Recommended Products: ${productListPage ? productListPage.trim() : this.isWmap() ? 'WMAP' : pageType === 'Product' ? 'Product' : pageType === 'Category' ? 'Category' : this.currentPageType}`],
                'product_impression_position': [`${position + 1}`],
                'product_impression_id': [`${productID}`],
                'product_impression_name': [product.ProductName],
                'product_impression_category': [
                    `${await getUtagProductCategory(
                        getUtagProductCategoryProps.actionContext,
                        getUtagProductCategoryProps.channelId,
                        getUtagProductCategoryProps.catalogId,
                        Number(getUtagProductCategoryProps.RecordId)
                    )}`
                ],
                'product_impression_brand': [brand],
                'product_impression_variant': [`${variant}`],
                'product_impression_price': prices.map(price => `${price.CustomerContextualPrice}`),
                // // adding the below variables for unbxd
                'unbxd_page_type': `${unbxdPageType.toUpperCase()}`,
                'unbxd_experience_widget': `${widgetNumber?.toUpperCase()}`,
                'd365_product_id': [`${product.MasterProductId}`],
                'd365_product_variant': [`${getUtagProductCategoryProps.RecordId}`]
            });
        }
    };

    private _getUnbxdRecommendationsData = async (): Promise<void> => {
        const {
            config: {
                pageType,
                widgetNumber,
                categoryLevelNames
            },
            data: {
                cart: { result }
            }
        } = this.props;
        const wmapCartItemIDs = getSetWmapCartLines('get');
        const cartLineItems = result?.cart.CartLines;
        const productItemID = this.props.data.product.result?.ItemId;
        let cartItemIDs = hasArray(cartLineItems)?.map((item) => {
            return item.ItemId;
        });
        if (!productItemID && wmapCartItemIDs?.length && this.isWmap()) {
            cartItemIDs = wmapCartItemIDs;
        }

        if (this.currentPageType === 'orderconfirmation') {
            const salesLines = (await this.props.data.mfrmSalesOrderDetails).salesOrder.SalesLines;
            cartItemIDs = hasArray(salesLines)?.map((item: SalesLine) => {
                return item.ItemId;
            });
        }
        try {
            getUnbxdRecommendationData(new GetUnbxdRecommendationsDataInput(pageType, widgetNumber, productItemID, categoryLevelNames, cartItemIDs), this.props.context.actionContext)
                .then((responseData: any) => {
                    if (responseData) {
                        this.setState({
                            count: responseData.unbxdRecs.count,
                            unbxdRecommendationData: responseData.unbxdRecs.recommendations,
                            widgetTitle: responseData.unbxdRecs.widgetTitle,
                            unbxdRequestId: responseData.unbxdRequestId ? responseData.unbxdRequestId : ''
                        });
                    }
                }).catch((e: any) => console.log(e));

        } catch (error) {
            console.log('Unbxd API error', Error);
        }
    };
    public render(): JSX.Element | null {
        const { carouselHeading } = this.props.config;
        const { unbxdRecommendationData } = this.state;

        const viewProps: IMfrmProductRecommendationCarouselViewProps = {
            ...(this.props as IMfrmProductRecommendationCarouselProps<IMfrmProductRecommendationCarouselData>),
            carouselItems: unbxdRecommendationData ? this._getCarouselItems(unbxdRecommendationData) : null,
            carouselHeading: carouselHeading ? carouselHeading : '',
            isMobile: this.state.isMobile,
            currentPageName: this.currentPageType
        };
        return this.props.renderView(viewProps);
    }

    private _getCarouselItems = (products: any[]): React.ReactNode[] | null => {
        if (products.length === 0 || products.length < 4) {
            return null;
        }
        const { context, app: { config: { wmapPageUrlSuffix, placeholderImageName } } } = this.props;
        const { ratingAriaLabel, startingAtText } = this.props.resources;
        return products.map((product, index) => {
            const unbxd_DefaultVariantID = product.Unbxd_DefaultVariantID;
            let productUrl = getProductPageUrlSync(removeSpecialChars(product?.ProductName!, context) || '', product.MasterProductId, context && this.props.context.actionContext, undefined);
            productUrl = productUrl?.replace(`/${wmapPageUrlSuffix}`, '');
            const variantIdQueryParams = unbxd_DefaultVariantID !== 0 && (productUrl.includes('?') ? `&variantid=${unbxd_DefaultVariantID}` : `?variantid=${unbxd_DefaultVariantID}`) || '';
            productUrl += variantIdQueryParams;
            const brandName = product && product.Attr_5637145355;
            return (
                <UnbxdProductCarouselItem
                    key={product.uniqueId}
                    position={index}
                    context={context}
                    product={product}
                    productUrl={productUrl}
                    brandName={brandName}
                    ratingAriaLabel={ratingAriaLabel}
                    startingAtText={startingAtText}
                    callUnbxdClickEvent={partialRight(this.handleUnbxdClickEvent, product)}
                    onView={partialRight(this.handleViewEvent, product, products)}
                    emptyPlaceHolderImage={placeholderImageName}
                />
            );
        });
    };
}

export default MfrmProductRecommendationCarousel;
